import AdminClient from '../client/AdminClient';
import _ from "lodash";
import {mapIds} from "../../../helpers/utils";

const firmTypes = {
    "EF": "Foundation",
    "RIA": "RiaInvestor",
    "FO": "Investor",
    "BT": "BankTrust",
};

class InvestorService {

    static async index(session, params) {
        const filterParams = this.parsedInvestorFilterParams(params.filterParams);
        return AdminClient.get(`/v1/admin/investors`, session,
            {
                ...params,
                filterParams: filterParams,
            }
        );
    }

    static async new(session) {
        return AdminClient.get(`/v1/admin/investors/new`, session);
    }

    static async create(session, investor) {
        return AdminClient.post(`/v1/admin/investors/`, session, this.parsedInvestor({...investor, status: "pending", draft_type: "create"}));
    }

    static async update(investorId, session, investor) {
        return AdminClient.put(`/v1/admin/investors/${investorId}`, session, this.parsedInvestor({...investor, draft_type: "update"}));
    }

    static async edit(investorId, session) {
        return AdminClient.get(`/v1/admin/investors/${investorId}/edit`, session);
    }

    static async show(investorId, session) {
        return AdminClient.get(`/v1/admin/investors/${investorId}`, session);
    }

    static async delete(investorId, session) {
        return AdminClient.delete(`/v1/admin/investors/${investorId}`, session);
    }

    static async removeOtherEntity(investorId, session, entityType) {
        return AdminClient.delete(`/v1/admin/investors/${investorId}/remove_other_entity`, session, { entity_type: entityType });
    }

    static async changeLog(investorId, session) {
        return AdminClient.get(`/v1/admin/investors/${investorId}/change_log`, session);
    }

    static async notes(investorId, session) {
        return AdminClient.get(`/v1/admin/investors/${investorId}/notes`, session);
    }

    static async uploadAvatar(investorId, session, file) {
        const formData = new FormData();
        formData.append('avatar', file);
        return AdminClient.uploadFile(`/v1/admin/investors/${investorId}/upload_avatar`, session, formData);
    }

    static async news(investorId, session) {
        return AdminClient.get(`/v1/admin/investors/${investorId}/news`, session);
    }

    static async relationships(investorId, session) {
        return AdminClient.get(`/v1/admin/investors/${investorId}/relationships`, session);
    }

    static async investments(investorId, session) {
        return AdminClient.get(`/v1/admin/investors/${investorId}/investment_history`, session);
    }

    static async realEstateInvestment(investorId, session) {
        return AdminClient.get(`/v1/admin/investors/${investorId}/real_estate_investment_history`, session);
    }

    static async contacts(investorId, session, dataType) {
        return AdminClient.get(`/v1/admin/investors/${investorId}/contacts`, session, {data_type: dataType});
    }

    static async historicalAums(investorId, session) {
        return AdminClient.get(`/v1/admin/investors/${investorId}/historical_aums`, session);
    }

    static async investors(query, session) {
        return AdminClient.get(`/v1/admin/investors/search`, session, {query});
    }

    static async markAsReviewed(investorId, session) {
        return AdminClient.put(`/v1/admin/investors/${investorId}/mark_as_reviewed`, session);
    }

    static async regenerateAiBio(investorId, session) {
        return AdminClient.put(`/v1/admin/investors/${investorId}/regenerate_ai_bio`, session);
    }

    /* Drafts */

    static async draftIndex(session, params) {
        const filterParams = this.parsedInvestorFilterParams(params.filterParams);
        return AdminClient.get(`/v1/admin/investor_drafts`, session,
            {
                ...params,
                filterParams: filterParams,
            }
        );
    }

    static async draftNew(session) {
        return AdminClient.get(`/v1/admin/investor_drafts/new`, session);
    }

    static async draftCreate(session, investor_draft) {
        return AdminClient.post(`/v1/admin/investor_drafts/`, session, this.parsedInvestorDraft({...investor_draft, status: "pending", draft_type: "create"}));
    }

    static async draftUpdate(investorDraftId, session, investor_draft) {
        return AdminClient.put(`/v1/admin/investor_drafts/${investorDraftId}`, session, this.parsedInvestorDraft({...investor_draft, draft_type: "update"}));
    }

    static async draftEdit(investorDraftId, session) {
        return AdminClient.get(`/v1/admin/investor_drafts/${investorDraftId}/edit`, session);
    }

    static async draftShow(investorDraftId, session) {
        return AdminClient.get(`/v1/admin/investor_drafts/${investorDraftId}`, session);
    }

    static async draftDelete(investorDraftId, session) {
        return AdminClient.delete(`/v1/admin/investor_drafts/${investorDraftId}`, session);
    }

    static async draftChangeLog(investorDraftId, session) {
        return AdminClient.get(`/v1/admin/investor_drafts/${investorDraftId}/change_log`, session);
    }

    static async draftNotes(investorDraftId, session) {
        return AdminClient.get(`/v1/admin/investor_drafts/${investorDraftId}/notes`, session);
    }

    static async draftUploadAvatar(investorDraftId, session, file) {
        const formData = new FormData();
        formData.append('avatar', file);
        return AdminClient.uploadFile(`/v1/admin/investor_drafts/${investorDraftId}/upload_avatar`, session, formData);
    }

    static async approve(investorDraftId, session) {
        return AdminClient.post(`/v1/admin/investor_drafts/${investorDraftId}/approve`, session);
    }

    static async reject(investorDraftId, session) {
        return AdminClient.post(`/v1/admin/investor_drafts/${investorDraftId}/reject`, session);
    }

    static async extraData(session) {
        return AdminClient.get(`/v1/admin/investor_drafts/extra_data`, session);
    }

    static async draftNews(draftId, session) {
        return AdminClient.get(`/v1/admin/investor_drafts/${draftId}/news`, session);
    }

    static async draftRelationships(draftId, session) {
        return AdminClient.get(`/v1/admin/investor_drafts/${draftId}/relationships`, session);
    }

    static async draftInvestments(draftId, session) {
        return AdminClient.get(`/v1/admin/investor_drafts/${draftId}/investment_history`, session);
    }

    static async draftRealEstateInvestments(draftId, session) {
        return AdminClient.get(`/v1/admin/investor_drafts/${draftId}/real_estate_investment_history`, session);
    }

    static async draftContacts(draftId, session) {
        return AdminClient.get(`/v1/admin/investor_drafts/${draftId}/contact_drafts`, session);
    }

    /* Drafts */

    static parsedInvestorDraft(investorDraft) {
        const {
            item_stage_name, investor_type_name, photo_url,
            family_wealth_origin_name, created, updated, researcher_name,
            notes_count, ...filteredDraft
        } = investorDraft;

        const relatedEntities = _.flatten(
            investorDraft.related_entities_attributes?.map(
                ({key, related_investor_id, related_investor_name, relationships, _destroy}) => {
                    return relationships.map(({id, relationship_id, relationship_name}) => ({
                        relationship_id,
                        related_investor_id,
                        _destroy
                    }))
                }
            )
        );

        const {contactDrafts, related_entities_attributes, contacts_attributes, ...parsedDraft} = {
            ...filteredDraft,
            investor_summary: investorDraft.investor_summary.replace(/\r?\n/g, "\r\n"), //Replace new lines with CRLF
            draft_related_entities_attributes: relatedEntities,
            news_attributes: investorDraft.news_attributes?.map(({key, extra_data, ...attributes}) => ({...attributes})),
            tracked_investments_attributes: investorDraft.tracked_investments_attributes?.map(({key, funded_company, funded_company_name, ...attributes}) => ({...attributes})),
            tracked_real_estate_investments_attributes: investorDraft.tracked_real_estate_investments_attributes?.map(({key, funded_property, funded_property_name, ...attributes}) => ({...attributes})),
            contactDrafts: investorDraft.contacts_attributes?.map((
                {
                    key, university, company, photoPreview, photo, photo_url,
                    position_name, contact_universities_attributes, company_contacts_attributes, email_validation_data,
                    ...attributes
                }
            ) => (
                    {
                        contact_universities_attributes: contact_universities_attributes?.map(({key, name, start_year, end_year, ...uniAtt}) => ({
                            start_date: `${start_year}-01-01`,
                            end_date: end_year ? `${end_year}-01-01` : null,
                            ...uniAtt
                        })),
                        company_contact_drafts_attributes: company_contacts_attributes?.map(({key, name, start_year, end_year, ...comAtt}) => ({
                            start_date: `${start_year}-01-01`,
                            end_date: end_year ? `${end_year}-01-01` : null,
                            ...comAtt
                        })),
                        ...attributes
                    }
                )
            ),
        };

        const formData = new FormData();
        formData.append('investor_draft', JSON.stringify(parsedDraft));
        contactDrafts.forEach(({newPhoto, ...attributes}) => {
            formData.append(`contact_drafts[][attributes]`, JSON.stringify(attributes));
            formData.append(`contact_drafts[][photo]`, newPhoto || '');
        })

        return formData;
    }

    static parsedInvestor(investor) {
        const {
            item_stage_name, investor_type_name, photo_url,
            family_wealth_origin_name, created, updated, researcher_name, last_reviewed, latest_update,
            notes_count, draft_type, ...filteredInvestor
        } = investor;

        const relatedFirms = _.flatten(
            investor.relatable_firms_attributes?.map(
                ({related_id, related_type, relationships, _destroy}) => {
                    const idParts = String(related_id).split('-');
                    const resolvedType = firmTypes[idParts[0]] || related_type || 'BankTrust';
                    const resolvedId = firmTypes[idParts[0]] ? idParts[1] : related_id;
                    return (relationships || []).map(({relationship_id}) => ({
                        related_id: resolvedId,
                        related_type: resolvedType,
                        relationship_id,
                        _destroy,
                    }));
                }
            ) || []
        );

        const {contacts, ...parsedInvestor} = {
            ...filteredInvestor,
            investor_summary: investor.investor_summary.replace(/\r?\n/g, "\r\n"), //Replace new lines with CRLF
            relatable_firms_attributes: relatedFirms,
            news_attributes: investor.news_attributes?.filter((news) => news.id === undefined).map(({key, extra_data, ...attributes}) => ({...attributes})),
            tracked_investments_attributes: investor.tracked_investments_attributes?.filter((investment) => investment.id === undefined).map(({key, funded_company, funded_company_name, ...attributes}) => ({...attributes})),
            tracked_real_estate_investments_attributes: investor.tracked_real_estate_investments_attributes?.filter((investment) => investment.id === undefined).map(({key, funded_property, funded_property_name, ...attributes}) => ({...attributes})),
            investor_historical_aums_attributes: investor.investor_historical_aums_attributes?.filter((investment) => investment.id === undefined).map(({key, ...attributes}) => ({...attributes})),
            contacts: investor.contacts_attributes?.map((
                {
                    key, university, company, photoPreview, photo, photo_url,
                    position_name, contact_universities_attributes, company_contacts_attributes,
                    contact_notes, notes_count, email_validation_data, ...attributes
                }
            ) => (
                    {
                        contact_universities_attributes: contact_universities_attributes?.map(({key, name, start_year, end_year, ...uniAtt}) => ({
                            start_date: `${start_year}-01-01`,
                            end_date: end_year ? `${end_year}-01-01` : null,
                            ...uniAtt
                        })),
                        company_contacts_attributes: company_contacts_attributes?.map(({key, name, start_year, end_year, ...comAtt}) => ({
                            start_date: `${start_year}-01-01`,
                            end_date: end_year ? `${end_year}-01-01` : null,
                            ...comAtt
                        })),
                        ...attributes
                    }
                )
            ),
        };

        const formData = new FormData();
        formData.append('investor', JSON.stringify(parsedInvestor));
        contacts.filter((contact) => contact.id === undefined).forEach(({newPhoto, ...attributes}) => {
            formData.append(`contacts[][attributes]`, JSON.stringify(attributes));
            formData.append(`contacts[][photo]`, newPhoto || '');
        })

        return formData;
    }

    static parsedInvestorFilterParams(params) {
        if (!_.isEmpty(params)) {
            return {
                ...params,
                researchers: mapIds(params.researchers),
                familyOfficeTypes: mapIds(params.familyOfficeTypes),
                investorTypes: mapIds(params.investorTypes),
                familyWealthOrigins: mapIds(params.familyWealthOrigins),
                countries: mapIds(params.countries),
                cities: mapIds(params.cities),
                states: mapIds(params.states),
                regions: mapIds(params.regions),
                geographicFocuses: mapIds(params.geographicFocuses),
                statuses: mapIds(params.statuses),
                stages: mapIds(params.stages),
            }
        } else {
            return params;
        }
    }
}
export default InvestorService;